import type {
  CreatePurchaseReturnRequest,
  PartnerSupplierItemHistory,
  PurchaseReturnResponse,
  UpdatePurchaseReturnRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { PARTNERS_KEY } from '@/features/partners/api';
import { MOVEMENTS_KEY, STOCK_LEVELS_KEY } from '@/features/inventory/stock-api';
import { throwApiError } from '@/lib/server-messages';

import { BILLS_KEY } from './api';

/** PUR-04 data layer — the debit note against a posted supplier bill. */

export const PURCHASE_RETURNS_KEY = ['purchase-returns'] as const;

export interface PurchaseReturnFilters {
  partnerId?: string | undefined;
  billId?: string | undefined;
  status?: PurchaseReturnResponse['status'] | undefined;
  /** Wave 3B — the list's window on the return date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

/**
 * Wave 3B — whether the list draws its search box: `true` the moment the
 * contract takes `q` (the annotation flips and the typecheck demands it).
 */
export const PURCHASE_RETURNS_SEARCHABLE: 'q' extends keyof PurchaseReturnFilters ? true : false = false;

function purchaseReturnQuery(filters: PurchaseReturnFilters) {
  return {
    ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    ...(filters.billId ? { billId: filters.billId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** PUR-04's list, one page at a time (NFR-42). All three filters run in SQL. */
export function usePurchaseReturns(
  filters: PurchaseReturnFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...PURCHASE_RETURNS_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.purchaseReturns.list({
        query: { ...purchaseReturnQuery(filters), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchPurchaseReturnsPage(
  filters: PurchaseReturnFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: PurchaseReturnResponse[]; total: number }> {
  const response = await api.purchaseReturns.list({ query: { ...purchaseReturnQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.returns, total: response.body.total };
}

/** Wave 3B — PUR-04's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function usePurchaseReturnStats() {
  return useQuery({
    queryKey: [...PURCHASE_RETURNS_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.purchaseReturns.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function usePurchaseReturn(returnId: string | undefined) {
  return useQuery({
    queryKey: [...PURCHASE_RETURNS_KEY, 'one', returnId],
    enabled: Boolean(returnId),
    queryFn: async (): Promise<PurchaseReturnResponse> => {
      const response = await api.purchaseReturns.get({ params: { returnId: returnId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * Wave 5D — the supplier's history with one item, for the DIRECT return's
 * free lines: the last posted bill price fills the unit price ahead of the
 * item's cost price, and bought − sent back is the figure the over-bought
 * hint reads. Off until both ids are known.
 */
export function useSupplierItemHistory(partnerId: string | undefined, itemId: string | undefined) {
  return useQuery({
    queryKey: [...PURCHASE_RETURNS_KEY, 'supplier-item-history', partnerId, itemId],
    enabled: Boolean(partnerId) && Boolean(itemId),
    queryFn: async (): Promise<PartnerSupplierItemHistory> => {
      const response = await api.partners.supplierItemHistory({
        params: { partnerId: partnerId! },
        query: { itemId: itemId! },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreatePurchaseReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePurchaseReturnRequest): Promise<PurchaseReturnResponse> => {
      const response = await api.purchaseReturns.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_RETURNS_KEY }),
  });
}

export function useUpdatePurchaseReturn(returnId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePurchaseReturnRequest): Promise<PurchaseReturnResponse> => {
      const response = await api.purchaseReturns.update({ params: { returnId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_RETURNS_KEY }),
  });
}

export function useDeletePurchaseReturn(returnId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.purchaseReturns.remove({ params: { returnId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_RETURNS_KEY }),
  });
}

/**
 * Post return — Draft → Posted. Three things move at once and all three are
 * on screen somewhere else: the GOODS leave the warehouse (stock), what we
 * OWE falls (the supplier's balance), and the bill it is against now carries
 * a debit note. So all three caches are dropped.
 */
export function usePostPurchaseReturn(returnId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PurchaseReturnResponse> => {
      const response = await api.purchaseReturns.post({ params: { returnId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PURCHASE_RETURNS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
      void queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_KEY });
      void queryClient.invalidateQueries({ queryKey: MOVEMENTS_KEY });
    },
  });
}

/** BR-PUR-04-01: cancel a POSTED debit note, reason required (GEN-72). */
export function useCancelPurchaseReturn(returnId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<PurchaseReturnResponse> => {
      const response = await api.purchaseReturns.cancel({ params: { returnId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PURCHASE_RETURNS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
      void queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_KEY });
      void queryClient.invalidateQueries({ queryKey: MOVEMENTS_KEY });
    },
  });
}

/** BR-PUR-04-02: the supplier sent money back — DR bank / CR 2111 (§12 row 4). */
export function useRefundPurchaseReturn(returnId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (depositToAccountId: string): Promise<PurchaseReturnResponse> => {
      const response = await api.purchaseReturns.refund({
        params: { returnId },
        body: { depositToAccountId },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PURCHASE_RETURNS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}
